<template>
  <div
    v-if="dashboard"
    class="dashboard-container"
  >
    <catalog-banner :dashboard="dashboard" />
    <delayed-purchase-banner :dashboard="dashboard" />
    <rate-limit-counter-container
      :source="dashboard"
      class="rate-limit-counter-container"
    />
    <checklist
      v-if="showChecklist"
      :checklist="checklist"
      :dashboard="dashboard"
    />
    <question-sets-promo-card />
    <div class="dashboard-widget">
      <mastery-panel
        :config="transitionPanelMessage"
        @cta-action="startTransition"
      />
      <mastery-panel :config="mergeBannerConfig" />
      <div
        v-if="shouldShowMira"
        class="mira-wrapper"
        :class="{ 'span-12': hasPerformanceOverallWidget }"
      >
        <mira-button context-type="home" />
      </div>
      <div
        v-for="(w, index) in displayedWidgets"
        :key="w.widget.id"
        class="widget-wrapper"
        :class="widgetClasses[index] || ''"
      >
        <component
          :is="widgetComponentName(w.widget)"
          v-if="showWidget(w.widget)"
          v-disableable="{blur: true, dim: true, disabled: $store.getters['session/onTour'] && widgetComponentName(w.widget) !== 'search-widget'}"
          :config="w.widget"
          :dashboard="dashboard"
          :identifier="w.id"
          :user-hideable="w.userHideable"
        />
      </div>
    </div>
  </div>
</template>

<script>
import BlockWidget from '@components/dashboard-widgets/blockWidget';
import CarouselWidget from '@components/dashboard-widgets/carouselWidget';
import CarouselDeckWidget from '@components/dashboard-widgets/CarouselDeckWidget';
import CatalogWidget from '@components/dashboard-widgets/catalogWidget';
import Checklist from '@components/dashboard-widgets/checklist';
import QuestionSetsPromoCard from '@components/question-sets/PromoCard';
import CountdownWidget from '@components/dashboard-widgets/countdownWidget';
import CustomQuizWidget from '@components/dashboard-widgets/CustomQuizWidget';
import DirectoryWidget from '@components/dashboard-widgets/directoryWidget';
import LessonWidget from '@components/dashboard-widgets/LessonWidget';
import MediaLibraryWidget from '@components/dashboard-widgets/mediaLibraryWidget';
import PossibilityOfPassingWidget from '@components/dashboard-widgets/possibilityOfPassingWidget';
import RateLimitWidget from '@components/dashboard-widgets/rateLimitWidget';
import RateLimitCounterContainer from '@components/banners/RateLimitCounterContainer';
import QuestionOfTheDayWidget from '@components/dashboard-widgets/questionOfTheDayWidget';
import QuestionsOfTheDayWidget from '@components/dashboard-widgets/QuestionsOfTheDayWidget';
import RecommendedQueueWidget from '@components/dashboard-widgets/recommendedQueueWidget';
import RecommendedTodayWidget from '@components/dashboard-widgets/recommendedTodayWidget';
import ReportsWidget from '@components/dashboard-widgets/ReportsWidget';
import ResumeWidget from '@components/dashboard-widgets/resumeWidget';
import PerformanceOverallWidget from '@components/dashboard-widgets/performanceOverallWidget';
import PerformanceCategoryWidget from '@components/dashboard-widgets/performanceCategoryWidget';
import PerformancePossibilityOfPassingWidget from '@components/dashboard-widgets/performancePossibilityOfPassingWidget';
import PerformanceQuestionsAnsweredWidget from '@components/dashboard-widgets/performanceQuestionsAnsweredWidget';
import SearchWidget from '@components/dashboard-widgets/searchWidget';
import ShowcaseWidget from '@components/dashboard-widgets/showcaseWidget';
import SocialProofWidget from '@components/dashboard-widgets/socialProofWidget';
import StartWidget from '@components/dashboard-widgets/StartWidget';
import StreakAndGoalWidget from '@components/dashboard-widgets/StreakAndGoalWidget';
import TestPrepWidget from '@components/dashboard-widgets/testPrepWidget';
import TilesWidget from '@components/dashboard-widgets/tilesWidget';
import TodayWidget from '@components/dashboard-widgets/todayWidget';
import UpgradeWidget from '@components/dashboard-widgets/upgradeWidget';

import CatalogBanner from '@components/banners/catalog-banner';
import DelayedPurchaseBanner from '@components/banners/delayed-purchase-banner.vue';
import MasteryModal from '@components/ui/masteryModal';
import MasteryPanel from '@components/ui/masteryPanel';
import MiraButton from '@components/mira/MiraButton';
import AiEventsMixin from '@/mixins/aiEvents';
import QuestionsUIV2Mixin from '@/mixins/questions-ui-v2';

import { shouldEnterTour, showTourModal } from '@/utils/tour';
import { showTransitionMessage, showPostTransitionMessage, showTransitionBanner } from '@/utils/transition';
import { getChecklist, hasChecklist, ONBOARDING_CHECKLIST_KEY } from '@/utils/checklist';
import { startTransitionQuestionnaire } from '@/utils/questionnaire';
import { userShouldSeeShareModal, snakeToPascal } from '@/utils';
import GlobalMixin from '@/mixins/global';
import { computeWidgetClasses } from '@views/dashboards/widgetLayout';

export default {
  name: 'DashboardShow',
  components: {
    BlockWidget,
    CarouselWidget,
    CarouselDeckWidget,
    CatalogBanner,
    CatalogWidget,
    CountdownWidget,
    CustomQuizWidget,
    DelayedPurchaseBanner,
    DirectoryWidget,
    Checklist,
    MasteryModal,
    MasteryPanel,
    MiraButton,
    LessonWidget,
    MediaLibraryWidget,
    PossibilityOfPassingWidget,
    QuestionSetsPromoCard,
    RateLimitWidget,
    RateLimitCounterContainer,
    PerformanceOverallWidget,
    PerformanceCategoryWidget,
    PerformancePossibilityOfPassingWidget,
    PerformanceQuestionsAnsweredWidget,
    QuestionOfTheDayWidget,
    QuestionsOfTheDayWidget,
    RecommendedQueueWidget,
    RecommendedTodayWidget,
    ReportsWidget,
    ResumeWidget,
    SearchWidget,
    ShowcaseWidget,
    SocialProofWidget,
    StartWidget,
    StreakAndGoalWidget,
    TestPrepWidget,
    TilesWidget,
    TodayWidget,
    UpgradeWidget,
  },
  mixins: [GlobalMixin, QuestionsUIV2Mixin, AiEventsMixin],
  data() {
    return {
      initialized: false,
      transitionPanelMessage: null,
    };
  },
  computed: {
    dashboard() {
      const apptimizeDashboardId = this.$apptimize.checkExperiments({
        experimentName: 'Alternate Home Dashboard Id',
        int: 'alternateHomeDashboardID',
        defaultVal: 0,
        matcher: /Alternate Home Dashboard Id/,
      });
      let alternativeDashboard;
      if (!isNaN(Number(apptimizeDashboardId)) && Number(apptimizeDashboardId) > 0) {
        alternativeDashboard = this.$store.getters['dashboards/get'][apptimizeDashboardId];
      }
      const defaultDashboardId = this.$store.getters['dashboards/menuDefaultDashboard']?.id;

      if (this.$route.name === 'mastery#home') {
        if (alternativeDashboard && alternativeDashboard.id) {
          this.updateRoute(alternativeDashboard.id);
          return this.$store.getters['dashboards/get'][alternativeDashboard.id];
        }
        return this.$store.getters['dashboards/menuDefaultDashboard'];
      } else if (this.$route.params.dashboardId === defaultDashboardId && (alternativeDashboard && alternativeDashboard.id)) {
        this.updateRoute(apptimizeDashboardId);
        return this.$store.getters['dashboards/get'][apptimizeDashboardId];
      } else {
        const id = Number(this.$route.params.dashboardId);
        return this.$store.getters['dashboards/get'][id];
      }
    },
    checklist() {
      return getChecklist(this.$store, ONBOARDING_CHECKLIST_KEY);
    },
    checklistWidget() {
      return this.widgets?.find(w => w.widget.widgetType === 'checklist');
    },
    displayedWidgets() {
      // We need to filter our the checklist widget so that it does not throw errors or display in the wrong spot
      return (this.widgets || [])
        .filter(w => {
          return this.registeredComponents.includes(this.componentName(w.widget.widgetType)) || w.widget.widgetType === 'custom_quiz';
        })
        .filter(w => w.widget.widgetType !== 'checklist')
        .filter(w => {
          if (w.widget.widgetType === 'upgrade') {
            return !this.hasPurchasedUpgrade(w.widget);
          }
          return true;
        })
        .filter(w => {
          if (w.widget.widgetType === 'reports') {
            return this.$store.getters['progressReports/all'].length > 0;
          }
          return true;
        });
    },
    registeredComponents() {
      return Object.keys(this.$options.components);
    },
    hasPerformanceOverallWidget() {
      return this.displayedWidgets.some(w => w.widget.widgetType === 'performance_overall');
    },
    widgetClasses() {
      return computeWidgetClasses(this.displayedWidgets);
    },
    homeSharingEnabled() {
      return !!(this.$store.getters['productVars/get']['homeShareMessage']);
    },
    mergeBannerConfig() {
      const showMergeBanner = this.$store.getters['ui/showMergeBanner'];
      if (showMergeBanner) {
        const pvar = this.$store.getters['productVars/get']['merged_app_url'];
        if (pvar) {
          return {
            title: 'We\'ve updated!',
            text: `Welcome to the new & improved My Mastery experience! If you\'d prefer to use the old version, <a href="${pvar.value}">click here</a>.`,
          };
        }
      }
    },
    showChecklist() {
      return hasChecklist(this.$store, this.checklist);
    },
    transitionBannerText() {
      return 'It’s important to keep your profile updated to receive relevant content. Add results as soon as you receive them.';
    },
    user() {
      return this.$store.getters['user/get'];
    },
    widgets() {
      if (this.dashboard) {
        let widgets = this.dashboard.appDashboardsDashboardWidgets;
        widgets.sort((a, b) => (a.order - b.order));

        if (!this.user?.previewer) {
          widgets = widgets.filter(w => !w.hidden);
        }
        return widgets.map(w => {
          const widget = this.$store.getters['dashboardWidgets/widgets'][w.dashboardWidgetId];
          return {
            ...w,
            widget,
            component: this.componentName(widget.widgetType),
          };
        });
      } else {
        return [];
      }
    },
  },
  created() {
    this.$store.dispatch('flashcardStates/fetchStates', ['bookmarks', 'categories', 'flashcards', 'stats', 'attachments', 'topics', 'learning_modules']);
  },
  mounted() {
    this.checkDefault();
    this.load();
    if (!this.dashboard) {
      /* Default dashboard doesn't exist take the user to
         the default navigation item instead.
      */
      let defaultNavigationItem = this.$store.getters['navigationItems/defaultNavigationItem'];
      if (defaultNavigationItem && defaultNavigationItem.route) {
        this.$router.push(defaultNavigationItem.route);
        return;
      }
    }
    this.$watch('dashboard', (dashboard) => {
      if (dashboard) {
        this.checkDashboardAccess(dashboard);
        const isOnDashboardGroupRoute = this.$route?.name === 'dashboard#group';
        if (!isOnDashboardGroupRoute) {
          this.$store.commit('dashboards/setLastVisitedId', [dashboard.id]);
        }
        this.fetchIconAttachments();
        this.fetchProgressReports();
        this.sendAnalyticsEvent();
      }
    }, { immediate: true });
  },
  methods: {
    checkDefault() {
      if (this.$route.name === 'mastery#home') {
        const destinations = ['dashboard', 'dashboard_group'];
        let menuDashboards = this.$store.getters['dashboards/menuDashboards'];
        menuDashboards = menuDashboards.filter( menuDashboard => destinations.includes(menuDashboard.destination) );
        menuDashboards = menuDashboards.sort( (a, b) => { return a.order > b.order; } );
        const menuDashboard = menuDashboards[0];
        if( menuDashboard?.destination == 'dashboard_group' ) {
          // this pushes back to the dashboard group route
          this.$router.push(menuDashboard.route);
        }
      }
    },
    checkDashboardAccess(dashboard) {
      if (dashboard?.hiddenShowOnPurchase) {
        const canAccess = this.$access(this.$store, 'appDashboard', dashboard.id);
        if (!canAccess) {
          this.$router.push({ name: 'mastery#home' });
        }
      }
    },
    fetchIconAttachments() {
      const ids = this.displayedWidgets.map(widget => widget.iconAttachmentId);
      this.$store.dispatch('attachments/fetch', { ids });
    },
    async load() {
      if (this.initialized) return;
      this.initialized = true;

      const upgradeQuery = this.$route.query.upgrade;
      const onHomeRoute = ['home', 'mastery#home'].includes(this.$route.name);
      if (onHomeRoute && (upgradeQuery === 'true' || upgradeQuery === true)) {
        await this.resetRoute();
        this.$store.dispatch('ui/toggleUpsellModal', { show: true, source: { type: 'Deeplink' }});
      }

      let transitionMessageConfig;
      const pastDue = this.$store.getters['purchaseReceipts/all'].find(pr => pr.pastDue && pr.active);
      if (this.$route.query?.po) {
        await this.showDeeplinkedPurchaseOrder(Number(this.$route.query.po));
      } else if (pastDue) {
        const pastDueModalSeen = sessionStorage.getItem('pastDueModalSeen');
        if (!pastDueModalSeen) {
          this.$modal.show('payment-error');
        }
      } else if (shouldEnterTour(this.$store)) {
        // if user should start tour, launch tour
        showTourModal.apply(this);
      } else if (transitionMessageConfig = showTransitionMessage(this.$store)) {
        this.$modal.show('mastery-transition', transitionMessageConfig);
      } else if (this.$route.query.transition === 'true' || this.$route.query.transition === true) {
        // vue router may save boolean param as boolean, but on hard reload it is a string
        const classification = this.$store.getters['classifications/get'][this.$route.query.classification];
        const postTransitionMessage = showPostTransitionMessage(this.$store, classification);
        if (postTransitionMessage) {
          await this.resetRoute();
          this.$modal.show('mastery-transition', postTransitionMessage);
        } else {
          await this.resetRoute();
        }
      } else if (this.homeSharingEnabled && userShouldSeeShareModal(this.$store)) {
        this.$modal.show('share-modal');
      }

      this.transitionPanelMessage = showTransitionBanner(this.$store);
    },
    async resetRoute() {
      await this.$router.replace({ name: 'mastery#home' });
      await this.$nextTick();
    },
    sendAnalyticsEvent() {
      let navItem;
      const menuNavGroup = this.$store.getters['navigationGroups/menuNavigationGroup'];
      if (this.$route.name === 'dashboard#group') {
        const dashboardGroupNavItem = menuNavGroup?.navigationItems(this.$store).find(ni => ni.destination === 'dashboard_group');
        const dashboardGroup = this.$store.getters['navigationGroups/navigationGroups'][dashboardGroupNavItem?.resourceId];
        navItem = dashboardGroup?.navigationItems(this.$store).find(ni => ni.resourceId === this.dashboard.id);
      } else {
        navItem = this.currentResource;
        if (!navItem?.name) {
          navItem = menuNavGroup?.navigationItems(this.$store).find(ni => ni.resourceId === this.dashboard.id);
        }
      }
      this.$analytics.events.viewedDashboard(navItem);
    },
    async showDeeplinkedPurchaseOrder(purchaseOrderId) {
      let actionName = 'ui/toggleUpsellModal';
      const upsellParams = { show: true, source: { type: 'Deeplink' }};
      const catalogPOs = this.$store.getters['purchaseOrders/addon'];
      if (catalogPOs?.length > 0) {
        const purchaseOrder = catalogPOs.find(po => po.id === purchaseOrderId);
        if (purchaseOrder) {
          let rootId;
          if (purchaseOrder.parentId) {
            const parent = catalogPOs.find(po => po.id === purchaseOrder.parentId);
            rootId = parent?.id;
          } else {
            rootId = purchaseOrder.id;
          }

          const page = this.$store.getters['catalog/pageList']?.find(p => p.purchaseEnabled && p.purchaseOrderId === rootId);
          if (page) {
            actionName = 'ui/toggleCatalogModal';
            upsellParams.catalogPageId = page.id;
          }
        }
      }

      await this.resetRoute();
      this.$store.dispatch(actionName, upsellParams);
    },
    showWidget(widget) {
      // learning path is not useable without the new question ui
      if (widget.name === 'Start' && !this.questionUiV2Enabled) {
        return false;
      }
      return true;
    },
    startTransition(config) {
      this.$analytics.events.selectedBanner({ text: config.text });
      startTransitionQuestionnaire.apply(this);
    },
    widgetComponentName(w) {
      if (w) {
        if (this.questionUiV2Enabled && w.widgetType === 'question_of_the_day') {
          return 'questions-of-the-day-widget';
        }
        return w.widgetType.split('_').join('-') + '-widget';
      } else {
        return null;
      }
    },
    updateRoute(expectedId) {
      this.$router.push({ ...this.$route, params: { ...this.$route.params, dashboardId: expectedId }});
    },
    hasPurchasedUpgrade(widget) {
      const jsonConfig = widget.config || {};
      const purchaseOrderId = jsonConfig.purchaseOrderId;

      if (purchaseOrderId) {
        const catalogPages = this.$store.getters['catalog/pageList'] || [];
        const catalogPage = catalogPages.find(page => page.purchaseOrderId === Number(purchaseOrderId));

        if (catalogPage) {
          const activePurchaseOrders = this.$store.getters['purchaseReceipts/active'].map(pr => pr.purchaseOrderId);
          const catalogPurchaseOrders = this.$store.getters['purchaseOrders/all']
            .filter(po => po.parentId === purchaseOrderId && !po.isFree)
            .map(po => po.id);
          return catalogPurchaseOrders.some(poId => activePurchaseOrders.includes(poId));
        }
      }

      return this.$store.getters['session/hasPaid'];
    },
    componentName(widgetType) {
      return snakeToPascal(widgetType) + 'Widget';
    },
    fetchProgressReports() {
      if ((this.widgets || []).some(w => w.widget.widgetType === 'reports')) {
        this.$store.dispatch('progressReports/fetchProgressReports');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dashboard-container {
  background-color: var(--bg-secondary);
  display: flex;
  align-items: center;
  flex-direction: column;

  > .dashboard-widget {
    display: grid;
    align-items: stretch;
    grid-template-columns: repeat(12, 1fr);
    column-gap: 20px;
    row-gap: 20px;
    width: 100%;
    max-width: 1260px;

    > div {
      grid-column: span 12;
      grid-row: span 1;
      align-content: baseline;
      max-width: rem-calc(768);
      margin: 0 auto;
      padding-top: 0;
      padding-bottom: 0;

      @include breakpoint(desktop) {
        width: 100%;
      }

      @include breakpoint(tablet) {
        width: 100%;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .dashboard-container > .dashboard-widget {
    row-gap: 56px;

    > div {
      margin: 0;
    }
  }
}

@media screen and (min-width: 1200px) {
  .dashboard-container > .dashboard-widget > div {

    &.span-12 {
      grid-column: span 12;
      width: 100%;
      max-width: unset;
    }

    &.span-9 {
      grid-column: span 9;
      width: 100%;
      max-width: unset;
    }

    &.span-8 {
      grid-column: span 8;
      width: 100%;
      max-width: unset;
    }

    &.span-6 {
      grid-column: span 6;
      width: 100%;
      max-width: unset;
    }

    &.span-4 {
      grid-column: span 4;
      width: 100%;
      max-width: unset;
      align-content: stretch;
    }

    &.span-3 {
      grid-column: span 3;
      width: 100%;
      max-width: unset;
    }
  }
}

.rate-limit-counter-container {
  width: calc(100% + 4rem);
  margin-top: -2rem;
  margin-bottom: 2rem;
}

.mira-wrapper {
  display: flex;
  justify-content: flex-end;
}
</style>
